import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, PAGES, PAGE_INFO, SETTINGS_TABS, SETTINGS_TAB_INFO } from '../contexts/PermissionContext';
import { FUNCTIONS_URL } from '../firebase';
import toast from 'react-hot-toast';

/**
 * 用戶權限編輯 Modal
 */
const UserPermissionModal = ({ user, isOpen, onClose, onSuccess }) => {
  const { getAuthHeader, userProfile: currentUser } = useAuth();
  const {
    permissionTags,
    getVisibleTags,
    hasWildcardAccess,
  } = usePermission();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    is_active: true,
    page_permissions: {
      reports: { enabled: false },
      projects: { enabled: false },
      settings: { enabled: false, tabs: [] },
    },
    visible_tags: [],
  });

  // 初始化表單資料
  useEffect(() => {
    if (user && isOpen) {
      const visibleTags = user.visible_tags || [];
      const targetUserIsAdmin = visibleTags.includes('*');

      // 如果目標用戶是管理員，頁面權限全部啟用
      const pagePermissions = targetUserIsAdmin ? {
        reports: { enabled: true },
        projects: { enabled: true },
        settings: {
          enabled: true,
          tabs: [SETTINGS_TABS.PERMISSION_MANAGEMENT, SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT, SETTINGS_TABS.SYSTEM_CONFIG, SETTINGS_TABS.SYSTEM_LOGS],
        },
      } : {
        reports: { enabled: user.page_permissions?.reports?.enabled || false },
        projects: { enabled: user.page_permissions?.projects?.enabled || false },
        settings: {
          enabled: user.page_permissions?.settings?.enabled || false,
          tabs: user.page_permissions?.settings?.tabs || [],
        },
      };

      // 如果目標用戶是管理員，資料標籤顯示全部勾選
      const effectiveVisibleTags = targetUserIsAdmin
        ? ['*', ...permissionTags.map(t => t.name)]
        : visibleTags;

      setFormData({
        display_name: user.display_name || '',
        is_active: user.status !== 'suspended' && user.is_active !== false,
        page_permissions: pagePermissions,
        visible_tags: effectiveVisibleTags,
      });
    }
  }, [user, isOpen, permissionTags]);

  if (!isOpen || !user) return null;

  const myTags = getVisibleTags();
  const isAdmin = hasWildcardAccess();
  const isEditingSelf = user.uid === currentUser?.uid;
  const isCreatedByMe = user.created_by === currentUser?.uid;

  // 檢查是否可以管理此用戶：
  // 1. 管理員可以管理任何人
  // 2. 非管理員只能管理自己創建的用戶，且不能管理自己
  const canManage = isAdmin || (isCreatedByMe && !isEditingSelf);

  // 管理員可以編輯所有設定
  const canEditSelfCritical = isAdmin;

  // 取得可指派的標籤 (只有自己 visible_tags 內的標籤)
  const assignableTags = isAdmin
    ? permissionTags
    : permissionTags.filter((tag) => myTags.includes(tag.name));

  // 頁面權限變更
  const handlePagePermissionChange = (page, enabled) => {
    setFormData((prev) => ({
      ...prev,
      page_permissions: {
        ...prev.page_permissions,
        [page]: { ...prev.page_permissions[page], enabled },
      },
    }));
  };

  // 設定子功能權限變更
  const handleSettingsTabChange = (tab, enabled) => {
    setFormData((prev) => {
      const currentTabs = prev.page_permissions.settings.tabs || [];
      const newTabs = enabled
        ? [...currentTabs, tab]
        : currentTabs.filter((t) => t !== tab);

      return {
        ...prev,
        page_permissions: {
          ...prev.page_permissions,
          settings: {
            ...prev.page_permissions.settings,
            tabs: newTabs,
          },
        },
      };
    });
  };

  // 標籤權限變更（可見標籤）
  const handleTagChange = (tagName, enabled) => {
    setFormData((prev) => {
      const currentTags = prev.visible_tags || [];
      const newTags = enabled
        ? [...currentTags, tagName]
        : currentTags.filter((t) => t !== tagName);

      return {
        ...prev,
        visible_tags: newTags,
      };
    });
  };

  // 提交表單
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const headers = await getAuthHeader();

      // 過濾掉 "*" 進行驗證
      const tagsToValidate = formData.visible_tags.filter(t => t !== '*');

      // 檢查是否可以設定目標用戶的標籤
      if (!isAdmin && !tagsToValidate.every((tag) => myTags.includes(tag))) {
        toast.error('您無法指派自己沒有的標籤');
        setLoading(false);
        return;
      }

      // 如果是管理員（有 *），只儲存 ["*"]，不儲存個別標籤
      const hasWildcard = formData.visible_tags.includes('*');
      const visibleTagsToSave = hasWildcard ? ['*'] : formData.visible_tags.filter(t => t !== '*');

      const response = await fetch(`${FUNCTIONS_URL}/api/users/${user.uid}/permissions`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: formData.display_name,
          is_active: formData.is_active,
          page_permissions: formData.page_permissions,
          visible_tags: visibleTagsToSave,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('用戶權限已更新');
        onSuccess?.();
        onClose();
      } else {
        toast.error(result.error?.message || '更新失敗');
      }
    } catch (error) {
      console.error('Error updating user permissions:', error);
      toast.error('更新失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl my-8 mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            編輯用戶權限：{user.display_name || user.email}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!canManage ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-yellow-800">
              {isEditingSelf
                ? '您無法編輯自己的權限，請聯繫管理員。'
                : '您只能管理自己創建的用戶。'}
            </p>
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          {/* 基本資料 */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>基本資料</span>
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  姓名
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, display_name: e.target.value }))
                  }
                  disabled={!canManage}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500"
                  value={user.email}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  狀態
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      checked={formData.is_active}
                      onChange={() => setFormData((prev) => ({ ...prev, is_active: true }))}
                      disabled={!canManage || !canEditSelfCritical}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm">啟用</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      checked={!formData.is_active}
                      onChange={() => setFormData((prev) => ({ ...prev, is_active: false }))}
                      disabled={!canManage || !canEditSelfCritical}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm">停用</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-6" />

          {/* 頁面權限 */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>頁面權限</span>
            </h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex flex-wrap gap-4">
                {Object.entries(PAGES).map(([key, pageId]) => {
                  const pageInfo = PAGE_INFO[pageId];
                  return (
                    <label key={pageId} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.page_permissions[pageId]?.enabled || false}
                        onChange={(e) => handlePagePermissionChange(pageId, e.target.checked)}
                        disabled={!canManage}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm">
                        {pageInfo?.icon} {pageInfo?.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 可見的資料標籤 */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>可見的資料標籤</span>
            </h4>
            <div className="bg-gray-50 rounded-lg p-4">
              {assignableTags.length === 0 ? (
                <p className="text-sm text-gray-500">沒有可指派的權限標籤</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {assignableTags.map((tag) => {
                    const isChecked = formData.visible_tags.includes(tag.name);
                    const isDisabled = !canManage || (!isAdmin && !myTags.includes(tag.name));

                    return (
                      <label
                        key={tag.id}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isDisabled ? 'opacity-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleTagChange(tag.name, e.target.checked)}
                          disabled={isDisabled}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <span
                          className="px-2 py-1 rounded text-xs text-white"
                          style={{ backgroundColor: tag.color || '#3B82F6' }}
                        >
                          {tag.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {!isAdmin && (
                <p className="text-xs text-gray-500 mt-3">
                  灰色選項表示您自己沒有該權限，無法指派給他人
                </p>
              )}
            </div>
          </div>

          {/* 設定頁面子功能 */}
          {formData.page_permissions.settings.enabled && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>設定頁面子功能（若設定已啟用）</span>
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.page_permissions.settings.tabs?.includes(
                      SETTINGS_TABS.PERMISSION_MANAGEMENT
                    )}
                    onChange={(e) =>
                      handleSettingsTabChange(SETTINGS_TABS.PERMISSION_MANAGEMENT, e.target.checked)
                    }
                    disabled={!canManage}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm">
                    {SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_MANAGEMENT]?.icon}{' '}
                    {SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_MANAGEMENT]?.label}
                  </span>
                </label>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.page_permissions.settings.tabs?.includes(
                        SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT
                      )}
                      onChange={(e) =>
                        handleSettingsTabChange(SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT, e.target.checked)
                      }
                      disabled={!canManage}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-sm">
                      {SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT]?.icon}{' '}
                      {SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT]?.label}
                    </span>
                  </label>
                  <p className="text-xs text-blue-600 ml-6 mt-1">
                    開啟此權限可新建/編輯/刪除專案及報表
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  「系統設定」及「系統紀錄」僅管理員可見（visible_tags 包含 "*"）
                </p>
              </div>
            </div>
          )}

          {/* 按鈕 */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading || !canManage}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? '儲存中...' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserPermissionModal;
